import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Admin.css';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const sentinel = useRef(null);

  // AI Review Stats
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewFrom, setReviewFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [reviewTo, setReviewTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [regenningUserId, setRegenningUserId] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    adminAPI.getStats().then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadTab = useCallback(async (pageNum, append) => {
    if (tab === 'overview') return;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      if (tab === 'users') {
        const data = await adminAPI.getUsers({ page: pageNum, search, limit: 20 });
        setUsers(prev => append ? [...prev, ...data.users] : data.users);
        setHasMore(pageNum < data.pages);
      } else if (tab === 'recipes') {
        const data = await adminAPI.getRecipes({ page: pageNum, search, limit: 20 });
        setRecipes(prev => append ? [...prev, ...data.recipes] : data.recipes);
        setHasMore(pageNum < data.pages);
      }
    } catch { /* ignore */ }
    if (append) setLoadingMore(false); else setLoading(false);
  }, [tab, search]);

  // Reset and load when tab or search changes
  useEffect(() => {
    if (tab === 'overview') return;
    setPage(1);
    loadTab(1, false);
  }, [loadTab]);

  // Load more pages
  useEffect(() => {
    if (page > 1) loadTab(page, true);
    // eslint-disable-next-line
  }, [page]);

  // IntersectionObserver
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          setPage(p => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading]);

  const handleDeleteUser = async (id, username) => {
    try {
      const info = await adminAPI.getUserInfo(id);
      const parts = [];
      if (info.recipeCount > 0) parts.push(`${info.recipeCount} recipe${info.recipeCount > 1 ? 's' : ''}`);
      if (info.conversationCount > 0) parts.push(`${info.conversationCount} conversation${info.conversationCount > 1 ? 's' : ''}`);
      if (info.followerCount > 0) parts.push(`${info.followerCount} follower${info.followerCount > 1 ? 's' : ''}`);
      let msg = `Delete user "${username}"?`;
      if (parts.length > 0) {
        msg += `\n\nThis will also remove their ${parts.join(', ')}, comments, and all associated data.`;
      }
      if (!window.confirm(msg)) return;
      await adminAPI.deleteUser(id);
      setUsers(prev => prev.filter(u => u._id !== id));
      setStats(prev => prev ? { ...prev, totalUsers: prev.totalUsers - 1 } : prev);
    } catch { /* ignore */ }
  };

  const handleRoleChange = async (id, role) => {
    try {
      await adminAPI.updateRole(id, role);
      setUsers(prev => prev.map(u => u._id === id ? { ...u, role } : u));
    } catch { /* ignore */ }
  };

  const handleDeleteRecipe = async (id) => {
    if (!window.confirm('Delete this recipe?')) return;
    try {
      await adminAPI.deleteRecipe(id);
      setRecipes(prev => prev.filter(r => r._id !== id));
      setStats(prev => prev ? { ...prev, totalRecipes: prev.totalRecipes - 1 } : prev);
    } catch { /* ignore */ }
  };

  const handleToggleDisable = async (id) => {
    try {
      const data = await adminAPI.toggleDisable(id);
      setUsers(prev => prev.map(u => u._id === id ? { ...u, disabled: data.user.disabled } : u));
    } catch { /* ignore */ }
  };

  const handleResetPassword = async (id, username) => {
    if (!window.confirm(`Reset password for ${username}?`)) return;
    try {
      const data = await adminAPI.resetPassword(id);
      window.alert(`New temporary password for ${username}:\n\n${data.tempPassword}`);
    } catch { /* ignore */ }
  };

  const handleRegenerateUserPhoto = async (id, username) => {
    setRegenningUserId(id);
    try {
      const data = await adminAPI.regenerateUserPhoto(id);
      setUsers(prev => prev.map(u => u._id === id ? { ...u, profilePicture: data.photoUrl } : u));
      window.alert(`Photo regenerated for ${username}`);
    } catch (err) {
      window.alert(`Failed: ${err.message}`);
    }
    setRegenningUserId(null);
  };

  const handleGenerateRecipe = async () => {
    setGenerating(true);
    setGenResult(null);
    try {
      const data = await adminAPI.generateRecipe();
      setGenResult({ success: true, message: data.message });
      setStats(prev => prev ? { ...prev, totalRecipes: prev.totalRecipes + 1 } : prev);
    } catch (err) {
      setGenResult({ success: false, message: err.message || 'Generation failed' });
    }
    setGenerating(false);
  };

  const loadReviewStats = useCallback(async () => {
    setReviewLoading(true);
    try {
      const data = await adminAPI.getReviewStats({ from: reviewFrom + 'T00:00:00.000Z', to: reviewTo + 'T23:59:59.999Z' });
      setReviewStats(data);
    } catch { setReviewStats(null); }
    setReviewLoading(false);
  }, [reviewFrom, reviewTo]);

  useEffect(() => {
    if (tab === 'reviews') loadReviewStats();
  }, [tab, loadReviewStats]);

  return (
    <div className="admin-page fade-in">
      <div className="admin-header">
        <h1>🔒 Admin Dashboard</h1>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => { setTab('overview'); setSearch(''); }}>Overview</button>
        <button className={`admin-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => { setTab('users'); setSearch(''); }}>Users</button>
        <button className={`admin-tab ${tab === 'recipes' ? 'active' : ''}`} onClick={() => { setTab('recipes'); setSearch(''); }}>Recipes</button>
        <button className={`admin-tab ${tab === 'reviews' ? 'active' : ''}`} onClick={() => { setTab('reviews'); setSearch(''); }}>AI Reviews</button>
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div>
          {loading ? <div className="loading-center"><div className="spinner" /></div> : stats && (
            <>
              <div className="stats-grid">
                <div className="stat-card card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-value">{stats.totalUsers}</div>
                  <div className="stat-label">Total Users</div>
                  <div className="stat-sub">+{stats.recentUsers} this week</div>
                </div>
                <div className="stat-card card">
                  <div className="stat-icon">📝</div>
                  <div className="stat-value">{stats.totalRecipes}</div>
                  <div className="stat-label">Total Recipes</div>
                  <div className="stat-sub">+{stats.recentRecipes} this week</div>
                </div>
                <div className="stat-card card">
                  <div className="stat-icon">🌍</div>
                  <div className="stat-value">{stats.publicRecipes}</div>
                  <div className="stat-label">Public Recipes</div>
                  <div className="stat-sub">{stats.totalRecipes > 0 ? Math.round((stats.publicRecipes / stats.totalRecipes) * 100) : 0}% public</div>
                </div>
              </div>

              <div className="admin-section card">
                <h3>Top Categories</h3>
                <div className="top-categories">
                  {stats.topCategories.map(cat => (
                    <div key={cat._id} className="category-bar">
                      <span className="category-bar-label">{cat._id}</span>
                      <div className="category-bar-fill">
                        <div
                          className="category-bar-value"
                          style={{ width: `${(cat.count / (stats.topCategories[0]?.count || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="category-bar-count">{cat.count}</span>
                    </div>
                  ))}
                </div>
              </div>

            </>
          )}
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div>
          <input
            className="input-field"
            placeholder="Search users..."
            value={search}
            onChange={e => { setSearch(e.target.value); }}
            style={{ marginBottom: 16 }}
          />
          {loading ? <div className="loading-center"><div className="spinner" /></div> : (
            <>
              <div className="admin-table card">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th className="hide-mobile">Email</th>
                      <th className="hide-mobile">Role</th>
                      <th className="hide-mobile">Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u._id} className={u.disabled ? 'user-disabled' : ''}>
                        <td>
                          <div className="table-user">
                            <div className="author-avatar-sm">
                              {u.profilePicture ? <img src={u.profilePicture} alt={u.username} /> : <span>{u.username[0].toUpperCase()}</span>}
                            </div>
                            <div>
                              <Link to={`/profile/${u._id}`} className="recipe-link">{u.username}</Link>
                              {u.disabled && <span className="badge badge-red" style={{ marginLeft: 6, fontSize: 10 }}>Disabled</span>}
                            </div>
                          </div>
                        </td>
                        <td className="hide-mobile">{u.email}</td>
                        <td className="hide-mobile">
                          <span className={`badge ${u.role === 'admin' ? 'badge-orange' : 'badge-green'}`}>{u.role}</span>
                        </td>
                        <td className="hide-mobile">{new Date(u.joinDate).toLocaleDateString()}</td>
                        <td>
                          <div className="table-actions-wrap">
                            <select
                              value={u.role}
                              onChange={e => handleRoleChange(u._id, e.target.value)}
                              className="role-select hide-mobile"
                              disabled={u._id === user?._id}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                            <button
                              className={`btn btn-sm ${u.disabled ? 'btn-success' : 'btn-warning'}`}
                              onClick={() => handleToggleDisable(u._id)}
                              title={u.disabled ? 'Enable user' : 'Disable user'}
                              disabled={u._id === user?._id || u.role === 'admin'}
                            >
                              {u.disabled ? 'Enable' : 'Disable'}
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleResetPassword(u._id, u.username)}
                              title="Reset password"
                              disabled={u._id === user?._id || u.role === 'admin'}
                            >
                              Reset PW
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleRegenerateUserPhoto(u._id, u.username)}
                              title="Regenerate profile photo"
                              disabled={regenningUserId === u._id}
                            >
                              {regenningUserId === u._id ? "Regen..." : "Regen Photo"}
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(u._id, u.username)} disabled={u._id === user?._id || u.role === 'admin'}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loadingMore && (
                <div className="loading-center" style={{ padding: '20px 0' }}><div className="spinner" /></div>
              )}
              <div ref={sentinel} className="scroll-sentinel" />
            </>
          )}
        </div>
      )}

      {/* Recipes Tab */}
      {tab === 'recipes' && (
        <div>
          <div className="admin-section card" style={{ marginBottom: 16 }}>
            <h3>🤖 AI Recipe Generator</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>Generate a new recipe using AI and publish it from a random user.</p>
            <button
              className="btn btn-primary"
              onClick={handleGenerateRecipe}
              disabled={generating}
              style={{ minWidth: 180 }}
            >
              {generating ? 'Generating...' : '✨ Generate Recipe'}
            </button>
            {genResult && (
              <p style={{ marginTop: 10, color: genResult.success ? 'var(--success)' : 'var(--error)' }}>
                {genResult.message}
              </p>
            )}
          </div>

          <input
            className="input-field"
            placeholder="Search recipes..."
            value={search}
            onChange={e => { setSearch(e.target.value); }}
            style={{ marginBottom: 16 }}
          />
          {loading ? <div className="loading-center"><div className="spinner" /></div> : (
            <>
              <div className="admin-table card">
                <table>
                  <thead>
                    <tr>
                      <th>Recipe</th>
                      <th className="hide-mobile">Creator</th>
                      <th className="hide-mobile">Categories</th>
                      <th>Status</th>
                      <th className="hide-mobile">Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map(r => (
                      <tr key={r._id}>
                        <td>
                          <div className="table-user">
                            {r.images?.[0]?.imageURL && (
                              <img src={r.images[0].imageURL} alt={r.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
                            )}
                            <Link to={`/recipe/${r._id}`} className="recipe-link">{r.name}</Link>
                          </div>
                        </td>
                        <td className="hide-mobile">{r.createdBy?.username || 'Unknown'}</td>
                        <td className="hide-mobile">
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {r.categories?.slice(0, 3).map(c => <span key={c} className="badge badge-orange">{c}</span>)}
                          </div>
                        </td>
                        <td><span className={`badge ${r.isPublic ? 'badge-green' : 'badge-red'}`}>{r.isPublic ? 'Public' : 'Private'}</span></td>
                        <td className="hide-mobile">{new Date(r.creationDate).toLocaleDateString()}</td>
                        <td>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRecipe(r._id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loadingMore && (
                <div className="loading-center" style={{ padding: '20px 0' }}><div className="spinner" /></div>
              )}
              <div ref={sentinel} className="scroll-sentinel" />
            </>
          )}
        </div>
      )}

      {/* AI Reviews Tab */}
      {tab === 'reviews' && (
        <div>
          <div className="admin-section card" style={{ marginBottom: 16 }}>
            <h3>📋 AI Recipe Review Stats</h3>
            <div className="review-date-range">
              <div className="form-group review-date-field">
                <label>From</label>
                <input type="date" className="input-field" value={reviewFrom} onChange={e => setReviewFrom(e.target.value)} />
              </div>
              <div className="form-group review-date-field">
                <label>To</label>
                <input type="date" className="input-field" value={reviewTo} onChange={e => setReviewTo(e.target.value)} />
              </div>
              <button className="btn btn-primary btn-sm" onClick={loadReviewStats} disabled={reviewLoading}>
                {reviewLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {reviewLoading ? <div className="loading-center"><div className="spinner" /></div> : reviewStats && (
            <>
              <div className="stats-grid">
                <div className="stat-card card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-value">{reviewStats.total}</div>
                  <div className="stat-label">Total Reviews</div>
                </div>
                <div className="stat-card card">
                  <div className="stat-icon">✅</div>
                  <div className="stat-value" style={{ color: 'var(--success)' }}>{reviewStats.passed}</div>
                  <div className="stat-label">Passed</div>
                  <div className="stat-sub">{reviewStats.total > 0 ? Math.round((reviewStats.passed / reviewStats.total) * 100) : 0}%</div>
                </div>
                <div className="stat-card card">
                  <div className="stat-icon">❌</div>
                  <div className="stat-value" style={{ color: 'var(--danger)' }}>{reviewStats.failed}</div>
                  <div className="stat-label">Failed</div>
                  <div className="stat-sub">{reviewStats.total > 0 ? Math.round((reviewStats.failed / reviewStats.total) * 100) : 0}%</div>
                </div>
              </div>

              {reviewStats.reviews.length > 0 && (
                <div className="admin-table card" style={{ marginTop: 16 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Recipe</th>
                        <th>Result</th>
                        <th className="hide-mobile">Feedback</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewStats.reviews.map(r => (
                        <tr key={r._id}>
                          <td style={{ fontWeight: 500 }}>{r.recipeName}</td>
                          <td><span className={`badge ${r.approved ? 'badge-green' : 'badge-red'}`}>{r.approved ? 'Passed' : 'Failed'}</span></td>
                          <td className="hide-mobile" style={{ fontSize: 12, maxWidth: 300 }}>{r.feedback}</td>
                          <td style={{ fontSize: 12 }}>{new Date(r.reviewedAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
